import {Component, DestroyRef, EventEmitter, inject, OnDestroy, OnInit, Output} from '@angular/core';
import {
  BokkzAlgDataService,
  BokkzClient,
  ClientEntity,
  DossierItem,
  IntermediaryEntity,
  UitstelGegevens,
  WorkflowComponent,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute} from '@angular/router';
import {FormControl, FormGroup} from '@angular/forms';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {dispatchDeletedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import {TitleBarConfiguration} from '@snelstart/ui-title-bar';
import {IContextMenu} from '@snelstart/ui-context-menu';
import {ButtonConfiguration} from '@snelstart/ui-button';
import {CommonModule, DatePipe} from '@angular/common';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-workflow-list',
  templateUrl: './feature-tax-workflow-list.component.html',
  styleUrl: './feature-tax-workflow-list.component.scss',
  imports: [SharedUiTableModule, DatePipe, SharedUiIconModule, WorkflowComponent, CommonModule],
})
export class FeatureTaxWorkflowListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<any>();
  protected tableConfig!: TableConfiguration<SearchCriteria, DossierItem>;
  private readonly destroyRef = inject(DestroyRef);
  private readonly bokkzAlgDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  public intermediary = {} as IntermediaryEntity;
  public client = {} as ClientEntity;
  public dossierItemList = [] as DossierItem[];
  public sstRelationId = {} as string;

  public readonly titleBarConfig = TitleBarConfiguration.create({
    newItems: [{label: 'Nieuw dossieritem', onClick: () => this.showDialog()}],
  });

  readonly intermediaryEntity$: Observable<IntermediaryEntity> =
    this.bokkzAlgDataService.intermediaryEntity$.pipe(
      tap(data => {
        if (data.id === undefined) {
          this.bokkzClient.getIntermediary().subscribe(intermediary => {
            this.bokkzAlgDataService.updateIntermediary(intermediary);
          })
        } else {
          this.intermediary = data;
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

    readonly clientId$ = this.activatedRoute.params.pipe(
        map(params => params['clientId'] ?? ''),
        filter(clientId => !!clientId),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    readonly clientEntity$ = this.clientId$.pipe(
        switchMap(clientId => this.bokkzClient.getClient(clientId)),
        tap(client => {
            this.bokkzAlgDataService.updateClient(client);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntilDestroyed(this.destroyRef)
    );

    public dossierItemFg = new FormGroup({
    id: new FormControl<number | null>(null),
    clientId: new FormControl<number | null>(null),
    title: new FormControl<string | null>(null),
    creator: new FormControl<string | null>(null),
    dateCreation: new FormControl<Date | null>(null),
    dateDeadline: new FormControl<Date | null>(null),
    hasDeadline: new FormControl<boolean | null>(false),
    isChecked: new FormControl<boolean | null>(false),
    opmerkingen: new FormControl<string | null>(null),
    attachment: new FormControl<string[] | null>(null),
    nameAttachment: new FormControl<string | null>(null),
    checked: new FormControl<boolean | null>(false),
    type: new FormControl<string | null>(null),
    year: new FormControl<string | null>(null),
  });

  public dialogVisible = false;
  public focusedElementId: string | null = null;
  public formStructure: any;
  public year = new Date().getFullYear() - 1;
  public type = '';
  public showTable = false;
  public result: string | undefined;


  ngOnInit(): void {
    this.intermediaryEntity$.subscribe();
    this.clientEntity$.subscribe();
    this.clientId$.subscribe()
    this.activatedRoute.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params[0].toString().includes('tax-income')) {
        this.bokkzAlgDataService.updateSelectedService('IB');
      }
      if (params[0].toString().includes('tax-corporate')) {
        this.bokkzAlgDataService.updateSelectedService('VPB');
      }
      if (params[0].toString().includes('tax-allowance')) {
        this.bokkzAlgDataService.updateSelectedService('TSL');
      }
      if (params[0].toString().includes('pm-report')) {
        this.bokkzAlgDataService.updateSelectedService('JR');
      }
      if (params[0].toString().includes('tax-dividend')) {
        this.bokkzAlgDataService.updateSelectedService('DB');
      }
      if (params[0].toString().includes('tax-donation')) {
        this.bokkzAlgDataService.updateSelectedService('SB');
      }
      if (params[0].toString().includes('tax-vat')) {
        this.bokkzAlgDataService.updateSelectedService('BTW');
      }
      if (params[0].toString().includes('tax-wage')) {
        this.bokkzAlgDataService.updateSelectedService('WAGE');
      }
    });
    this.observeContext();
  }

  ngOnDestroy(): void {
    this.dossierItemFg.reset();
  }

  private observeContext(): void {
    this.bokkzAlgDataService.selectedService.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.type = value;
      this.showTable = false;
      setTimeout(() => {
        this.createTableConfig(this.year);
        this.showTable = true;
      }, 0);
    });
    this.bokkzAlgDataService.selectedYear.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.year = value;
      this.showTable = false;
      setTimeout(() => {
        this.createTableConfig(value);
        this.showTable = true;
      }, 0);
    });
  }

  private createTableConfig(year: number): void {
    const criteria = new SearchCriteria();
    this.tableConfig = TableConfiguration.create<SearchCriteria, DossierItem>({
      identifier: 'id',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: criteria,
      search: () =>
          this.clientId$.pipe(
              take(1), // important: table expects finite observable per search call
              switchMap(clientId =>
                  this.bokkzClient.getDossierItems(clientId).pipe(
                      map((allDossierItems: DossierItem[]) => {

                          if (!Array.isArray(allDossierItems) || allDossierItems.length === 0) {
                              return new SearchResult<UitstelGegevens>([], 0);
                          }

                          const yearStr = String(year);
                          const typeStr = String(this.type);

                          const filteredByYear = allDossierItems.filter(a =>
                              a.jaar?.startsWith(yearStr)
                          );

                          this.dossierItemList = filteredByYear;

                          const finalItems = filteredByYear
                              .filter(a => a.type?.startsWith(typeStr))
                              .reverse();

                          return new SearchResult(finalItems, finalItems.length);
                      })
                  )
              )
          )
        ,
      fromDetailsMap: (result) => result,
      details: (entity) => {
        if (entity != undefined) {
          this.details.next(entity.id);
        }
      },
      contextMenu: (item) => (menu: IContextMenu) => {
        const group = menu.createGroup();

        if (item.soort !== 'SnelStart') {
          if (item.attachment) {
            group.and.createGroup().addItem({
              icon: 'trash',
              iconType: 'light',
              name: $localize`Verwijderen`,
              confirm: {
                type: 'delete',
                title: 'Verwijderen dossier item & bijlage',
                body:
                  'Weet je zeker dat je het dossier item ' +
                  "'" +
                  item.actie +
                  "'" +
                  ' definitief wilt verwijderen? Let op: ook de bijbehorende bijlage wordt verwijderd!',
              },
              onClick: () =>
                new Observable(() => {
                  this.deleteDossierItem(item as DossierItem);
                }),
            });
          } else {
            group.and.createGroup().addItem({
              icon: 'trash',
              iconType: 'light',
              name: $localize`Verwijderen`,
              confirm: {
                type: 'delete',
                title: 'Verwijderen dossier item',
                body: 'Weet je zeker dat je het dossieritem ' + "'" + item.actie + "'" + ' definitief wilt verwijderen?',
              },
              onClick: () =>
                new Observable(() => {
                  this.deleteDossierItem(item as DossierItem);
                }),
            });
          }
        } else {
          group.and.createGroup().addItem({
            name: $localize`Verwijderen niet mogelijk`,
            colorType: 'danger',
          });
        }
        return group;
      },
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: $localize`Geen dossieritems aanwezig`,
        subText: ' ',
        buttonText: $localize`Nieuw dossieritem`,
        buttonConfig: ButtonConfiguration.create({
          onClick: () =>
            new Observable((subscriber) => {
              setTimeout(() => {
                this.showDialog();
                subscriber.next(true);
                subscriber.complete();
              }, 50);
            }),
        }),
      }),
    });
  }

  showDialog() {
    this.dossierItemFg.reset();
    this.dossierItemFg.patchValue(
      {
        creator: this.intermediary.officeName,
        clientId: this.client.id,
        dateCreation: new Date(),
        type: this.type,
        year: String(this.year) ?? undefined,
      },
      {emitEvent: false},
    );
    this.dialogVisible = true;
  }

  private deleteDossierItem(dossierItem: DossierItem) {
    if (this.client.sstRelationId && dossierItem) {
      this.bokkzClient.deleteDossierItem(this.client.sstRelationId, Number(dossierItem.id)).subscribe(
        (item: number) => {
          if (item) {
            dispatchDeletedItemEvent({identifier: String(item!)});
            if (dossierItem.attachment) {
              this.toastService.showSuccessMessage('Het dossier item en de bijlage zijn succesvol verwijderd.');
            } else {
              this.toastService.showSuccessMessage('Het dossier item is succesvol verwijderd.');
            }
          }
        },
        (error) => {
          if (dossierItem.attachment) {
            this.toastService.showErrorMessage('Het dossier item en de bijlage zijn niet verwijderd.');
          } else {
            this.toastService.showErrorMessage('Het dossier item is niet verwijderd.');
          }
        },
      );
    }
  }
}
